import { useEffect, useRef } from 'react';

import { useIsMutating } from '@tanstack/react-query';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { RadioProps } from '@telialt/common-input/src/model';
import { RadioButtonProps } from '@telialt/common-radio-button/src';
import { trackViewItemGA } from '@telialt/gtm-tracking/src/events';
import { Cart, CheckoutPaymentMethod, CheckoutPaymentMethods } from '@telialt/models/src/EshopModel';
import {
    selectedInsuranceFranchisePrice,
    selectInsuranceFreePeriod,
    selectInsuranceMonthlyPrice,
    selectIsCartFresh,
    selectProductInCartCode,
    selectProductStock,
} from '@telialt/utils/src/eshopSelectors';
import { withParams } from '@telialt/utils/src/helpers';
import { useSegment } from '@telialt/utils/src/segment';

import { Description } from './checkout/Description';
import { useCartQuery } from './hooks/queries/useCartQuery';
import { ESHOP_MUTATION_KEY } from './model';
import { isPaymentMethodDescriptionVisible } from './utils';

export const useInsuranceDisclaimer = (tooltipContent: string, cart: Cart): string => {
    const insuranceFreePeriod = selectInsuranceFreePeriod(cart);
    const monthlyPayment = selectInsuranceMonthlyPrice(cart)?.formattedValue;
    const franchisePrice = selectedInsuranceFranchisePrice(cart)?.formattedValue;

    return withParams(tooltipContent, {
        franchisePrice,
        noPaymentPeriod: insuranceFreePeriod,
        paymentStartPeriod: insuranceFreePeriod + 1,
        monthlyPayment,
    });
};

export const useCheckoutPaymentMethodsOptionsOld = (paymentMethods: CheckoutPaymentMethod[]): RadioProps[] => {
    const segment = useSegment();
    const { t } = useTranslations();

    if (!paymentMethods) {
        return [];
    }

    return paymentMethods.map((paymentMethod) => ({
        label: t[`eshop.checkout.payment.method.${paymentMethod}.label`],
        description: isPaymentMethodDescriptionVisible(segment, paymentMethod) && (
            <Description>{t[`eshop.checkout.payment.method.${paymentMethod}.description`]}</Description>
        ),
        value: paymentMethod,
        name: paymentMethod,
    }));
};

export const useCheckoutPaymentMethodsOptions = (paymentMethods: CheckoutPaymentMethod[]): RadioButtonProps[] => {
    const segment = useSegment();
    const { t } = useTranslations();

    if (!paymentMethods) {
        return [];
    }

    // Reorder so that bank method will be first
    const reorderedMethods: CheckoutPaymentMethod[] = paymentMethods.filter(
        (method) => method !== CheckoutPaymentMethods.Bank,
    );
    if (paymentMethods.includes(CheckoutPaymentMethods.Bank)) {
        reorderedMethods.unshift(CheckoutPaymentMethods.Bank);
    }

    return reorderedMethods.map((paymentMethod) => ({
        name: 'payment-method',
        label: t[`eshop.checkout.payment.method.${paymentMethod}.label`],
        value: paymentMethod,
        id: paymentMethod,
        helperText:
            isPaymentMethodDescriptionVisible(segment, paymentMethod) &&
            t[`eshop.checkout.payment.method.${paymentMethod}.description`],
    }));
};

const useIsLoadingGATriggers = () => {
    const isAddingEntry = useIsMutating({ mutationKey: [ESHOP_MUTATION_KEY.ADD_CART_ENTRY] });
    const isAddingRatePlan = useIsMutating({ mutationKey: [ESHOP_MUTATION_KEY.ADD_RATE_PLAN] });
    const isAddingLeasingPlan = useIsMutating({ mutationKey: [ESHOP_MUTATION_KEY.ADD_LEASING_PLAN] });
    const isDeletingRatePlan = useIsMutating({ mutationKey: [ESHOP_MUTATION_KEY.DELETE_RATE_PLAN] });
    const isDeletingLeasingPlan = useIsMutating({ mutationKey: [ESHOP_MUTATION_KEY.DELETE_LEASING_PLAN] });
    const isUpdatingRentingPlan = useIsMutating({ mutationKey: [ESHOP_MUTATION_KEY.UPDATE_RENTING_PLAN] });
    const isUpdatingInitialDeposit = useIsMutating({ mutationKey: [ESHOP_MUTATION_KEY.UPDATE_INITIAL_DEPOSIT] });

    return (
        isAddingEntry ||
        isAddingRatePlan ||
        isAddingLeasingPlan ||
        isDeletingRatePlan ||
        isDeletingLeasingPlan ||
        isUpdatingRentingPlan ||
        isUpdatingInitialDeposit
    );
};
export const useSendGAEventProductView = () => {
    const { cart } = useCartQuery();
    const productCode = selectProductInCartCode(cart);
    const isCartFresh = selectIsCartFresh(cart);
    const isLoading = useIsLoadingGATriggers();
    const trackedProductCodeRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (isCartFresh || isLoading || !cart || !productCode) {
            return;
        }

        if (trackedProductCodeRef.current === productCode) {
            return;
        }

        trackViewItemGA({ source: 'productPage', data: { cart } });
        trackedProductCodeRef.current = productCode;
    }, [cart, isCartFresh, isLoading, productCode]);
};

export const useProductStock = (cart: Cart) => {
    const stock = selectProductStock(cart);

    if (stock?.nextDeliveryDate) {
        const localizedStockShipmentDate = new Date(stock.nextDeliveryDate).toLocaleDateString('lt-LT', {
            month: 'long',
            day: 'numeric',
        });

        return {
            stock,
            localizedStockShipmentDate,
        };
    }

    return {
        stock,
        localizedStockShipmentDate: undefined,
    };
};
